import React, { FunctionComponent, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import {
  AuditDetail,
  DefaultCollateralIdentifierType,
  EntityTypeEnum,
  NegotiationHistorySubscriptionIdentifier,
  PlatformFeature,
  SubscriptionIdentifier
} from 'api'
import { CenterPanel } from 'view/routes/routesLayout'
import { DataGrid, filteredColumnDefsBuilder, GridAutoSizing, GridId } from 'view/components/datagrid'
import { CompletedNegotiationsHeroPanel } from './CompletedNegotiationsHeroPanel'
import { OrganisationFilterSwitch } from 'view/components/datagrid/toolbar/OrganisationFilterSwitch'
import { CompletedNegotiationsGroupByToggles } from './CompletedNegotiationsGroupByToggles'
import { NegotiationSubscriptionFilters } from 'view/components/datagrid/filters/NegotiationSubscriptionFilters'
import { completedGridColumnDefs, completedGridOptions } from './CompletedNegotiationsGrid/CompletedNegotiationsGridModel'
import { AdminOnlyColumnIds, CollectionColumnId } from 'view/routes/ColumnNames'
import { CompletedGridColumnId } from './CompletedNegotiationsGrid/CompletedGridColumnNames'
import { SubscriptionDetail } from 'store/selection/selectionTypes'
import { AuditActions } from 'api/AuditActions'
import { createEntitySnapshotLoadedSelector } from 'selectors/systemSelectors'
import { createNegotiationHistoryDetailsSelector } from 'selectors/auditSelectors'
import { isCurrentOrganisationBuySideSelector } from 'selectors/organisationSelectors'
import { createPlatformFeatureEnabledSelector } from 'selectors/platformSelectors'
import { createEntitySubscriptionSelector } from 'selectors/selectionSelectors'
import { defaultCollateralIdentifierTypeSelector } from 'selectors/userPreferenceSelectors'
import { GridApi } from 'ag-grid-community'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'
import { generateExportFileName } from 'view/components/datagrid/gridUtils/utils'
import { useChangedSubscriptionFilters } from 'view/shared/entity/hooks/useChangedSubscriptionFilters'

interface CompletedNegotiationsScreenProps {
  negotiationList: AuditDetail[]
  subscriptionDetail?: SubscriptionDetail
  snapshotLoaded: boolean
  isBuySideOrganisation: boolean
  allowPartialEnabled: boolean
  defaultCollateralIdentifierType: DefaultCollateralIdentifierType
  onPageSubscribe: typeof AuditActions.pageSubscribe
  isAdmin: boolean
}

const mapStateToProps = createStructuredSelector({
  negotiationList: createNegotiationHistoryDetailsSelector([NegotiationHistorySubscriptionIdentifier.HISTORICAL]),
  subscriptionDetail: createEntitySubscriptionSelector(
    () => EntityTypeEnum.NEGOTIATION_HISTORY,
    () => SubscriptionIdentifier.HISTORICAL
  ),
  snapshotLoaded: createEntitySnapshotLoadedSelector(EntityTypeEnum.NEGOTIATION), // TODO Might not be necessary
  isBuySideOrganisation: isCurrentOrganisationBuySideSelector,
  allowPartialEnabled: createPlatformFeatureEnabledSelector(PlatformFeature.ALLOW_PARTIAL),
  defaultCollateralIdentifierType: defaultCollateralIdentifierTypeSelector,
  isAdmin: isAdminGatewayUserSelector
})

const mapDispatchToProps = {
  onPageSubscribe: AuditActions.pageSubscribe
}

export const CompletedNegotiationsScreenComponent: FunctionComponent<CompletedNegotiationsScreenProps> = ({
  negotiationList,
  subscriptionDetail,
  snapshotLoaded,
  isBuySideOrganisation,
  allowPartialEnabled,
  defaultCollateralIdentifierType,
  onPageSubscribe,
  isAdmin
}) => {
  const onChangeSubscription = useChangedSubscriptionFilters(EntityTypeEnum.NEGOTIATION_HISTORY, SubscriptionIdentifier.HISTORICAL, true)

  useEffect(() => {
    onChangeSubscription(null)
    return () => {
      onChangeSubscription([])
    }
  }, [])

  const someNegotiationsHaveCollection = negotiationList.some((auditDetail: AuditDetail) => auditDetail.collection)
  const filteredColumnDefs = useMemo(
    () =>
      filteredColumnDefsBuilder(completedGridColumnDefs(isBuySideOrganisation))
        .withoutIf(!isAdmin, Object.values(AdminOnlyColumnIds))
        .withoutIf(!allowPartialEnabled, [CompletedGridColumnId.ALLOW_PARTIAL])
        .build(),
    [allowPartialEnabled, isBuySideOrganisation, isAdmin]
  )

  const onPageChanged = (pageNumber: number) => {
    if (subscriptionDetail) {
      onPageSubscribe(SubscriptionIdentifier.HISTORICAL, subscriptionDetail.filters, pageNumber)
    }
  }

  const handleAfterRestoreDefaultLayout = (gridApi: GridApi) => {
    gridApi.setColumnsVisible(
      [CompletedGridColumnId.COLLATERAL_ISIN_OR_BUI],
      defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.CUSIP
    )

    gridApi.setColumnsVisible(
      [CompletedGridColumnId.COLLATERAL_CUSIP],
      defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.ISIN
    )
  }

  return (
    <CenterPanel>
      <CompletedNegotiationsHeroPanel />
      <DataGrid
        gridId={GridId.CompletedGrid}
        gridOptions={completedGridOptions}
        gridColumnAutoSizing={GridAutoSizing.IGNORE_HEADERS}
        columnDefs={filteredColumnDefs}
        rowData={negotiationList}
        customToolbarSwitches={[OrganisationFilterSwitch]}
        GroupByToggles={CompletedNegotiationsGroupByToggles}
        lockedPrimaryRowGroupColumnIds={someNegotiationsHaveCollection ? [CollectionColumnId.COLLECTION_NAME] : []}
        allPossibleLockedPrimaryRowGroupColumnIds={[CollectionColumnId.COLLECTION_NAME]}
        subscriptionFilters={() => (
          <NegotiationSubscriptionFilters
            entityType={EntityTypeEnum.NEGOTIATION_HISTORY}
            subscriptionIdentifier={NegotiationHistorySubscriptionIdentifier.HISTORICAL}
            pageResults={true}
          />
        )}
        subscriptionDetail={subscriptionDetail}
        onPageChanged={onPageChanged}
        showLoader={!snapshotLoaded}
        onAfterRestoreDefaultLayout={handleAfterRestoreDefaultLayout}
        exportFileName={generateExportFileName('Historical')}
      />
    </CenterPanel>
  )
}

export const CompletedNegotiationsScreen = connect(mapStateToProps, mapDispatchToProps)(CompletedNegotiationsScreenComponent)
